import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import '../styles/CheckIn.css';
import { section } from 'framer-motion/client';

// Event dates for reference
const EVENT_DATES = {
  day1: 'Day 1 - October 14, 2024',
  day2: 'Day 2 - October 15, 2024',
  day3: 'Day 3 - October 16, 2024'
};

const CheckIn = () => {
  const [activeTab, setActiveTab] = useState('qr');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [selectedDay, setSelectedDay] = useState('day1');
  const [checkInStats, setCheckInStats] = useState({
    day1: { checkedIn: 0, total: 0 },
    day2: { checkedIn: 0, total: 0 },
    day3: { checkedIn: 0, total: 0 },
    totalRegistrations: 0
  });
  const html5QrCodeRef = useRef(null);
  const navigate = useNavigate();

  // Initialize html5-qrcode
  useEffect(() => {
    let isMounted = true;
    
    const startScanner = async () => {
      if (cameraEnabled && isMounted) {
        try {
          html5QrCodeRef.current = new Html5Qrcode("qr-reader");
          
          const qrCodeSuccessCallback = (decodedText, decodedResult) => {
            console.log('QR Code scanned:', decodedText);
            if (isMounted) {
              setScannedData(decodedText);
              handleSearch(decodedText);
              stopScanner();
            }
          };

          const config = { 
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0
          };

          await html5QrCodeRef.current.start(
            { facingMode: 'environment' },
            config,
            qrCodeSuccessCallback,
            (errorMessage) => {
              if (isMounted) {
                console.error('QR Code scan error:', errorMessage);
                setError('Failed to scan QR code. Please try again.');
              }
            }
          );
        } catch (err) {
          console.error('Error starting QR scanner:', err);
          if (isMounted) {
            setError('Failed to access camera. Please check permissions.');
            setCameraEnabled(false);
          }
        }
      }
    };

    startScanner();

    // Cleanup function
    return () => {
      isMounted = false;
      stopScanner();
    };
  }, [cameraEnabled]);

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        console.log('QR Code scanning stopped.');
      } catch (err) {
        console.error('Error stopping QR scanner:', err);
      }
    }
  };

  const toggleCamera = () => {
    if (cameraEnabled) {
      stopScanner();
      setCameraEnabled(false);
      setScannedData('');
    } else {
      setCameraEnabled(true);
    }
  };

  // Fetch check-in statistics on component mount
  useEffect(() => {
    const fetchCheckInStats = async () => {
      try {
        const apiUrl = import.meta.env.PROD 
          ? 'https://africa-maritime-talent.onrender.com' 
          : 'http://localhost:5002';
        
        const response = await fetch(`${apiUrl}/api/register/checkin/stats`);
        const data = await response.json();
        
        if (data.success) {
          setCheckInStats(data.data);
        }
      } catch (error) {
        console.error('Error fetching check-in stats:', error);
      }
    };
    
    fetchCheckInStats();
  }, []);

  const handleCheckIn = async (registrationId) => {
    if (!registrationId) return null;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const apiUrl = import.meta.env.PROD
        ? 'https://africa-maritime-talent.onrender.com'
        : 'http://localhost:5002';

      const response = await fetch(`${apiUrl}/api/register/checkin/${registrationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ day: selectedDay })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Check-in failed');
      }

      const registration = data.data;

      // Success UI feedback
      setSuccess(data.message || `Checked in for ${EVENT_DATES[selectedDay]}`);
      setSearchResults([registration]);

      // Refresh stats bar
      try {
        const statsResp = await fetch(`${apiUrl}/api/register/checkin/stats`);
        const statsJson = await statsResp.json();
        if (statsJson.success) {
          setCheckInStats(statsJson.data);
        }
      } catch (err) {
        console.error('Stats refresh error', err);
      }

      return registration;
    } catch (err) {
      setError(err.message || 'An error occurred during check-in');
      return null;
    } finally {
      setLoading(false);
    }
  };


  const handleSearch = async (scannedValue = null) => {
    let searchValue = scannedValue || searchTerm.trim();
    // If QR contains JSON, extract id or email
    try {
      const possibleObj = JSON.parse(searchValue);
      searchValue = possibleObj.registrationId || possibleObj.id || possibleObj.email || searchValue;
    } catch(_) {/* not JSON, keep original */}
    if (!searchValue) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const apiUrl = import.meta.env.PROD 
        ? 'https://africa-maritime-talent.onrender.com' 
        : 'http://localhost:5002';
      
      // Search by ID or email
      const response = await fetch(`${apiUrl}/api/register?search=${encodeURIComponent(searchValue)}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to search for registration');
      }
      
      // If we have a match, show success and display registration
      if (data.data && data.data.length > 0) {
        const registration = data.data[0];
        setSearchResults([registration]);
        
        // Check if already checked in for the selected day
        if (registration.checkIns?.[selectedDay]?.checkedIn) {
          setSuccess(`Already checked in for ${EVENT_DATES[selectedDay]}`);
        } else {
          // Automatically check in the user
          try {
            const updatedRegistration = await handleCheckIn(registration._id);
            if (updatedRegistration) {
              setSearchResults([updatedRegistration]);
              setSuccess(`Successfully checked in for ${EVENT_DATES[selectedDay]}!`);
            }
          } catch (error) {
            // If check-in fails, still show the registration but with an error
            setError(error.message);
          }
        }
      } else {
        setError('No registration found with that ID or email');
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleScan = (result) => {
    if (result && result.text) {
      console.log('QR Code scanned:', result.text);
      setScannedData(result.text);
      // Auto-search when a QR code is scanned
      handleSearch(result.text);
      // Turn off camera after successful scan
      setCameraEnabled(false);
    }
  };
  
  const handleError = (error) => {
    console.error('Error:', error);
    setError('An error occurred. Please try again.');
  };
  
  const getCheckInPercentage = (day) => {
    if (checkInStats[day]?.total === 0) return 0;
    return Math.round((checkInStats[day]?.checkedIn / checkInStats[day]?.total) * 100) || 0;
  };

  return ( 
    <section className="w-full " style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }} >

    <section className="checkin-container">
      <div className="checkin-header">
        <h1>Event Check-In</h1>
        <p>Scan QR code or search by ID/email to check in attendees</p>
        
        {/* Day Selection */}
        <div className="day-selection">
          <h3>Select Day:</h3>
          <div className="day-buttons">
            {Object.entries(EVENT_DATES).map(([day, label]) => (
              <button
                key={day}
                className={`day-button ${selectedDay === day ? 'active' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                {label}
                <span className="checkin-count">
                  {checkInStats[day]?.checkedIn || 0}/{checkInStats[day]?.total || 0} ({getCheckInPercentage(day)}%)
                </span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Check-in Stats */}
        <div className="checkin-stats">
          <div className="stat-card">
            <h4>Total Registrations</h4>
            <p className="stat-number">{checkInStats.totalRegistrations}</p>
          </div>
          {['day1', 'day2', 'day3'].map(day => (
            <div key={day} className="stat-card">
              <h4>{EVENT_DATES[day]}</h4>
              <p className="stat-number">
                {checkInStats[day]?.checkedIn || 0} / {checkInStats[day]?.total || 0}
                <span className="stat-percentage">({getCheckInPercentage(day)}%)</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'qr' ? 'active' : ''}`}
          onClick={() => setActiveTab('qr')}
        >
          QR Code
        </button>
        <button 
          className={`tab ${activeTab === 'phone' ? 'active' : ''}`}
          onClick={() => setActiveTab('phone')}
        >
          Phone Number
        </button>
        <button 
          className={`tab ${activeTab === 'name' ? 'active' : ''}`}
          onClick={() => setActiveTab('name')}
        >
          Name
        </button>
      </div>

      <div className="search-container">
        <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
          {activeTab === 'qr' && (
            <div className="qr-section">
              <div className="scanner-container">
                <div id="qr-reader" style={{ width: '100%', maxWidth: '500px', margin: '0 auto', display: cameraEnabled ? 'block' : 'none' }}></div>
                {!cameraEnabled ? (
                  <div className="qr-scanner-placeholder" onClick={toggleCamera}>
                    <div className="camera-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                        <circle cx="12" cy="13" r="4"></circle>
                      </svg>
                    </div>
                    <p className="hint">Click to enable camera for QR scanning</p>
                  </div>
                ) : (
                  <div className="w-full flex justify-center">
                    <button 
                      onClick={toggleCamera}
                      className="mt-4 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                        <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56"></path>
                      </svg>
                      Stop Camera
                    </button>
                  </div>
                )}
                
                <div className="or-divider">
                  <span>OR</span>
                </div>
                
                <div className="manual-input">
                  <input
                    type="text"
                    placeholder="Enter ticket number or QR code manually"
                    value={scannedData || searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                  />
                  <button 
                    type="button" 
                    onClick={handleSearch}
                    disabled={!(scannedData || searchTerm.trim()) || loading}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'phone' && (
            <div className="phone-search">
              <input
                type="tel"
                placeholder="Enter phone number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" disabled={!searchTerm.trim() || loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          )}

          {activeTab === 'name' && (
            <div className="name-search">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" disabled={!searchTerm.trim() || loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          )}
        </form>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {searchResults.length > 0 ? (
          <div className="registration-details">
            <h3>Registration Details</h3>
            {searchResults.map((registration) => (
              <div key={registration._id} className="registration-card">
                <div className="user-info">
                  <h4>{registration.title} {registration.firstName} {registration.lastName}</h4>
                  <p><strong>Email:</strong> {registration.email}</p>
                  <p><strong>Company:</strong> {registration.companyName}</p>
                  <p><strong>Package:</strong> {registration.paymentOption === '3day' ? '3-Day Full Access' : '2-Day Access'}</p>
                  
                  <div className="checkin-status">
                    <h4>Check-in Status:</h4>
                    <div className="checkin-days">
                      {['day1', 'day2', 'day3'].map(day => (
                        <div key={day} className={`checkin-day ${registration.checkIns?.[day]?.checkedIn ? 'checked-in' : ''}`}>
                          <span className="day-name">{EVENT_DATES[day].split(' - ')[0]}:</span>
                          {registration.checkIns?.[day]?.checkedIn ? (
                            <span className="status checked">
                              Checked In
                              <span className="checkin-time">
                                {new Date(registration.checkIns[day].checkInTime).toLocaleTimeString()}
                              </span>
                            </span>
                          ) : (
                            <span className="status pending">Not Checked In</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {!registration.checkIns?.[selectedDay]?.checkedIn ? (
                  <button 
                    onClick={() => handleCheckIn(registration._id)}
                    className="checkin-button"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : `Check In for ${EVENT_DATES[selectedDay].split(' - ')[0]}`}
                  </button>
                ) : (
                  <div className="checked-in-badge success">
                    <span>Checked In for {EVENT_DATES[selectedDay].split(' - ')[0]}</span>
                    <span className="checkin-time">
                      {new Date(registration.checkIns[selectedDay].checkInTime).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <div className="no-results">
            <p>Search for a registration using the form above or scan a QR code</p>
          </div>
        )}
      </div>
    </section>
  

    </section>

  );
};

export default CheckIn;
